'use client';

import {
  type OneTrustEnvironment,
  OneTrustProvider,
} from '@jouzen/onetrust-consent-manager';
import { useEnvContext } from 'next-runtime-env';
import type { ReactNode } from 'react';

import logToDatadog from '@/logger/logToDatadog';
import { getCookie } from '@/utils/cookie';

const OneTrustConsentProvider = ({
  children,
}: {
  readonly children: ReactNode;
}) => {
  const {
    NEXT_PUBLIC_OURA_ENV,
    NEXT_PUBLIC_SEGMENT_KEY,
    NEXT_PUBLIC_ONETRUST_DOMAIN_SCRIPT,
  } = useEnvContext();

  // Check if segment is disabled
  const isSegmentDisabled = getCookie('disable-segment') === 'true';

  // Check for missing environment variables and log appropriately
  const missingEnvVars: string[] = [];
  if (!NEXT_PUBLIC_OURA_ENV) missingEnvVars.push('NEXT_PUBLIC_OURA_ENV');
  if (!NEXT_PUBLIC_SEGMENT_KEY) missingEnvVars.push('NEXT_PUBLIC_SEGMENT_KEY');
  if (!NEXT_PUBLIC_ONETRUST_DOMAIN_SCRIPT)
    missingEnvVars.push('NEXT_PUBLIC_ONETRUST_DOMAIN_SCRIPT');

  // Only render if we have the required environment variables and segment is not disabled
  // Super edge case but ensures we don't break environments if initializing CM with misconfigured or missing env variables
  if (
    !NEXT_PUBLIC_OURA_ENV ||
    !NEXT_PUBLIC_SEGMENT_KEY ||
    !NEXT_PUBLIC_ONETRUST_DOMAIN_SCRIPT ||
    isSegmentDisabled
  ) {
    // Log missing environment variables
    if (missingEnvVars.length > 0) {
      logToDatadog({
        level: 'error',
        message: `OneTrustConsentProvider: Missing required environment variables: ${missingEnvVars.join(', ')}`,
        module: 'analytics',
        data: {
          missingEnvironmentVariables: missingEnvVars,
          component: 'OneTrustConsentProvider',
          segmentDisabled: isSegmentDisabled,
        },
      });
    }

    // Log if segment is disabled (info level since this might be intentional)
    if (isSegmentDisabled && missingEnvVars.length === 0) {
      logToDatadog({
        level: 'info',
        message:
          'OneTrustConsentProvider: Segment analytics disabled via cookie',
        module: 'analytics',
        data: {
          component: 'OneTrustConsentProvider',
          segmentDisabled: true,
        },
      });
    }

    return <>{children}</>;
  }

  return (
    <OneTrustProvider
      applicationName="ecom-website"
      environment={NEXT_PUBLIC_OURA_ENV as OneTrustEnvironment}
      onetrustKey={NEXT_PUBLIC_ONETRUST_DOMAIN_SCRIPT}
      segmentKey={NEXT_PUBLIC_SEGMENT_KEY}
      oneTrustLoadTimeoutMs={15000}
    >
      {children}
    </OneTrustProvider>
  );
};

export default OneTrustConsentProvider;
